/**
 * Enhanced Professional Audio Player UI
 * Pure UI component that reads from AudioPlayerContext.
 * Audio element is managed by PersistentAudioElement at the App level.
 *
 * Features:
 * - Full & minimized views
 * - Click-to-seek & drag progress bar
 * - Keyboard shortcuts (Space, ←, →, M)
 * - Queue drawer (upcoming tracks)
 * - Volume slider with persistence (via context)
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAudioPlayer } from '@/context/AudioPlayerContext';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Shuffle,
  Repeat,
  Repeat1,
  Loader2,
  Music,
  ChevronUp,
  ChevronDown,
  Volume2,
  VolumeX,
  ListMusic,
  X,
} from 'lucide-react';

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// ── Component ───────────────────────────────────────────────────────────────

export const EnhancedAudioPlayer: React.FC = () => {
  const { state, play, pause, nextTrack, previousTrack, toggleShuffle, setRepeat, setVolume, setIsDragging, seekTo, hidePlayer } = useAudioPlayer();
  const [isMinimized, setIsMinimized] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const lastSeekTimeRef = useRef(0);

  // ── Sidebar-aware positioning ──────────────────────────────────────────
  // Measure the <main> element's left offset to align with content area
  // (the sidebar naturally shifts <main> via flexbox)
  const [mainLeft, setMainLeft] = useState(0);
  const [mainRight, setMainRight] = useState(0);
  const playerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const measure = () => {
      const main = document.querySelector('main');
      if (main) {
        const rect = main.getBoundingClientRect();
        setMainLeft(rect.left);
        setMainRight(window.innerWidth - rect.right);
      }
    };

    // Measure on mount, resize, and whenever DOM might shift (sidebar toggle)
    measure();
    window.addEventListener('resize', measure);

    const observer = new ResizeObserver(measure);
    const main = document.querySelector('main');
    if (main) observer.observe(main);

    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, []);

  // ═════════════════════════════════════════════════════════════════════════
  // Progress bar — click-to-seek and drag
  // ═════════════════════════════════════════════════════════════════════════

  const seekFromMouseEvent = useCallback((clientX: number) => {
    if (!progressBarRef.current || !state.duration) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = clientX - rect.left;
    const paddingLeft = 16;
    const paddingRight = 16;
    const usableWidth = rect.width - paddingLeft - paddingRight;
    const usableClickX = clickX - paddingLeft;
    const percent = Math.max(0, Math.min(1, usableClickX / usableWidth));
    const newTime = percent * state.duration;
    lastSeekTimeRef.current = newTime;
    seekTo(newTime);
  }, [state.duration, seekTo]);

  const handleProgressBarMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    isDraggingRef.current = true;
    setIsDragging(true);
    seekFromMouseEvent(e.clientX);
  }, [seekFromMouseEvent, setIsDragging]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      seekFromMouseEvent(e.clientX);
    };
    const onMouseUp = () => {
      isDraggingRef.current = false;
      setIsDragging(false);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [seekFromMouseEvent, setIsDragging]);

  // ═════════════════════════════════════════════════════════════════════════
  // Keyboard shortcuts
  // ═════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input or textarea
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          if (state.isPlaying) pause();
          else play();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (state.currentTrack) {
            const newTime = Math.max(0, state.currentTime - 5);
            seekTo(newTime);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (state.currentTrack) {
            const newTime = Math.min(state.duration, state.currentTime + 5);
            seekTo(newTime);
          }
          break;
        case 'KeyM':
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            setVolume(state.isMuted ? 0.5 : 0);
          }
          break;
        case 'KeyN':
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            nextTrack();
          }
          break;
        case 'KeyP':
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            previousTrack();
          }
          break;
      }
    };

    // Only attach when the player is visible
    if (!state.showPlayer) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [state.showPlayer, state.isPlaying, state.currentTrack, state.currentTime, state.duration, play, pause, seekTo, setVolume, nextTrack, previousTrack]);

  // ═════════════════════════════════════════════════════════════════════════
  // Guard — don't render when there's nothing to play
  // ═════════════════════════════════════════════════════════════════════════

  if (!state.currentTrack || !state.showPlayer) {
    return null;
  }

  // ═════════════════════════════════════════════════════════════════════════
  // Render
  // ═════════════════════════════════════════════════════════════════════════

  return (
    <>
      {/* Queue Drawer */}
      {showQueue && (
        <QueueDrawer mainRight={mainRight} onClose={() => setShowQueue(false)} />
      )}

      <div
        ref={playerRef}
        className="fixed bottom-0 bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-t border-gray-200 dark:border-gray-800 shadow-2xl z-[100] transition-all duration-300"
        style={{ left: mainLeft, right: mainRight }}
      >

        {/* Timeline Display */}
        <div className="px-4 pt-3 pb-1 flex justify-between items-center text-xs text-gray-600 dark:text-gray-400 select-none">
          <span className="font-medium tabular-nums">{formatTime(state.currentTime)}</span>
          <span className="font-medium tabular-nums">{formatTime(state.duration)}</span>
        </div>

        {/* Progress Bar */}
        <div
          ref={progressBarRef}
          className={`relative cursor-pointer px-4 py-2 transition-all ${state.isDragging ? 'py-3' : 'py-2'}`}
          onMouseDown={handleProgressBarMouseDown}
          title={`${formatTime(state.currentTime)} / ${formatTime(state.duration)}`}
        >
          <div
            className={`transition-all ${
              state.isDragging ? 'h-3' : 'h-2'
            } bg-gray-200 dark:bg-gray-700 rounded-full relative overflow-hidden`}
          >
            {/* Buffered indicator */}
            {state.buffered > 0 && (
              <div
                className="absolute inset-y-0 left-0 bg-gray-300 dark:bg-gray-600 rounded-full pointer-events-none"
                style={{ width: `${state.buffered}%` }}
              />
            )}
            {/* Progress */}
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary-500 to-primary-600 transition-all rounded-full pointer-events-none"
              style={{ width: `${state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0}%` }}
            />
            {/* Thumb */}
            <div
              className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white dark:bg-gray-100 rounded-full shadow-lg transition-opacity pointer-events-none ${
                state.isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
              }`}
              style={{ left: `${state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0}%`, marginLeft: '-8px' }}
            />
          </div>
        </div>

        {/* ── Minimized Floating Window ── */}
        {isMinimized ? (
          <div
            className="fixed bottom-6 max-w-sm w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[200] overflow-hidden transition-all duration-300"
            style={{ right: Math.max(16, mainRight + 16) }}
          >
            <div className="flex items-center gap-3 p-3">
              {/* Album art placeholder */}
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0 shadow-md">
                <Music className="w-6 h-6 text-white" />
              </div>

              {/* Track info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight">
                  {state.currentTrack.title}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                  {state.currentTrack.artist}
                </p>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => (state.isPlaying ? pause() : play())}
                  className="p-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700 rounded-full transition text-white shadow-sm"
                  title={state.isPlaying ? 'Pause' : 'Play'}
                >
                  {state.isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : state.isPlaying ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => nextTrack()}
                  className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition text-gray-700 dark:text-gray-300"
                  title="Next track [N]"
                >
                  <SkipForward className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Expand & Close */}
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button
                  onClick={() => setIsMinimized(false)}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition"
                  title="Expand player"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={hidePlayer}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition text-gray-400 hover:text-red-500"
                  title="Close player"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
            {/* Thin progress bar at bottom */}
            <div className="h-1 bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all"
                style={{ width: `${state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0}%` }}
              />
            </div>
          </div>
        ) : (
          /* ── Full Player View ── */
          <>
            <div className="px-3 py-1.5 flex items-center gap-2">
              {/* Track Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {state.currentTrack.title}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                  {state.currentTrack.artist}
                </p>
              </div>

              {/* Time Display */}
              <div className="hidden sm:flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap flex-shrink-0 tabular-nums">
                <span>{formatTime(state.currentTime)}</span>
                <span>/</span>
                <span>{formatTime(state.duration)}</span>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => toggleShuffle()}
                  className={`p-1.5 rounded transition ${
                    state.shuffle
                      ? 'bg-primary-600 text-white dark:bg-primary-600'
                      : 'hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                  title={`Shuffle ${state.shuffle ? 'on' : 'off'} [S]`}
                >
                  <Shuffle className="w-3.5 h-3.5" />
                </button>

                <button
                  onClick={() => previousTrack()}
                  className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition text-gray-700 dark:text-gray-300"
                  title="Previous track [P]"
                >
                  <SkipBack className="w-3.5 h-3.5" />
                </button>

                <button
                  onClick={() => (state.isPlaying ? pause() : play())}
                  className="p-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700 rounded-full transition text-white"
                  title={state.isPlaying ? 'Pause [Space]' : 'Play [Space]'}
                >
                  {state.isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : state.isPlaying ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                </button>

                <button
                  onClick={() => nextTrack()}
                  className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition text-gray-700 dark:text-gray-300"
                  title="Next track [N]"
                >
                  <SkipForward className="w-3.5 h-3.5" />
                </button>

                <button
                  onClick={() => setRepeat(state.repeat === 'none' ? 'all' : state.repeat === 'all' ? 'one' : 'none')}
                  className={`p-1.5 rounded transition ${
                    state.repeat !== 'none'
                      ? 'bg-primary-600 text-white dark:bg-primary-600'
                      : 'hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                  title={`Repeat: ${state.repeat}`}
                >
                  {state.repeat === 'one' ? <Repeat1 className="w-3.5 h-3.5" /> : <Repeat className="w-3.5 h-3.5" />}
                </button>

                {/* Queue Toggle */}
                <button
                  onClick={() => setShowQueue(!showQueue)}
                  className={`p-1.5 rounded transition ${
                    showQueue
                      ? 'bg-primary-600 text-white dark:bg-primary-600'
                      : 'hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                  title="Play queue"
                >
                  <ListMusic className="w-3.5 h-3.5" />
                </button>

                {/* Minimize */}
                <button
                  onClick={() => setIsMinimized(true)}
                  className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition"
                  title="Minimize player"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Volume Control */}
              <div className="relative flex-shrink-0 hidden sm:block">
                <button
                  onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                  className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition"
                  title={`Volume [M to mute]`}
                >
                  {state.isMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                </button>
                  {showVolumeSlider && (
                  <div className="absolute bottom-12 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2 shadow-lg z-[110]">
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={state.volume}
                      onChange={(e) => setVolume(parseFloat(e.target.value))}
                      className="w-24 h-1 bg-gray-300 dark:bg-gray-600 rounded cursor-pointer"
                    />
                  </div>
                )}
              </div>

              {/* Close button */}
              <button
                onClick={hidePlayer}
                className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                title="Close player"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        )}

        {!isMinimized && (
          <div className="px-4 pb-1 text-[10px] text-gray-400 dark:text-gray-600 text-center select-none">
            Space: Play/Pause · ←/→: Seek · M: Mute · N: Next · P: Previous
          </div>
        )}
      </div>
    </>
  );
};

// ── Queue Drawer ────────────────────────────────────────────────────────────

function QueueDrawer({ mainRight, onClose }: { mainRight: number; onClose: () => void }) {
  const { state, nextTrack, setQueue, playTrack, setCurrentTrack, setIsPlaying } = useAudioPlayer();

  const handleRemoveFromQueue = (index: number) => {
    const currentTrackId = state.currentTrack?.id;
    const trackToRemove = state.playlist[index];

    // If removing the currently playing track, also clear currentTrack
    if (currentTrackId && trackToRemove?.id === currentTrackId) {
      const newQueue = [...state.playlist];
      newQueue.splice(index, 1);
      if (newQueue.length > 0) {
        // Advance to the next track (or previous if at the end)
        const nextIndex = Math.min(index, newQueue.length - 1);
        newQueue[nextIndex] = { ...newQueue[nextIndex] }; // ensure new ref
        setQueue(newQueue);
        // Play the next track
        setCurrentTrack(null);
        setTimeout(() => {
          playTrack(newQueue[nextIndex]);
        }, 0);
      } else {
        // Queue is empty — clear everything
        setQueue([]);
        setCurrentTrack(null);
        setIsPlaying(false);
      }
      return;
    }

    // Removing a non-current track — just splice and update the queue
    const newQueue = [...state.playlist];
    newQueue.splice(index, 1);
    setQueue(newQueue);
  };

  const handlePlayNow = (index: number) => {
    const remaining = state.playlist.slice(index);
    if (remaining.length > 0) {
      // Play the FIRST track in the new queue immediately
      const target = remaining[0];
      // setQueue first (updates playlist + recalculates currentIndex)
      // then playTrack (sets currentTrack + isPlaying + currentTime)
      setQueue(remaining);
      // Use setTimeout to let setQueue's state update propagate first
      setTimeout(() => {
        playTrack(target);
      }, 0);
    }
  };

  return (
    <div
      className="fixed bottom-24 w-80 max-h-96 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl z-[150] flex flex-col overflow-hidden"
      style={{ right: Math.max(16, mainRight + 16) }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <ListMusic className="w-4 h-4" />
          Up Next
          {state.playlist.length > 0 && (
            <span className="text-xs font-normal text-gray-500">{state.playlist.length} tracks</span>
          )}
        </h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {state.playlist.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            <Music className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p>Queue is empty</p>
            <p className="text-xs mt-1">Select a song to start playing</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {state.playlist.map((track, index) => {
              const isCurrent = state.currentTrack?.id === track.id;
              return (
                <div
                  key={`${track.id}-${index}`}
                  className={`flex items-center gap-3 px-4 py-2.5 group ${
                    isCurrent ? 'bg-primary-50 dark:bg-primary-950/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  {/* Position */}
                  <span className="w-5 text-xs text-gray-400 tabular-nums text-right flex-shrink-0">
                    {isCurrent ? '►' : index + 1}
                  </span>

                  {/* Track Info */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${isCurrent ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-900 dark:text-gray-100'}`}>
                      {track.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{track.artist}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handlePlayNow(index)}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-400 hover:text-primary-600"
                      title="Play from here"
                    >
                      <Play className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleRemoveFromQueue(index)}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-400 hover:text-red-500"
                      title="Remove from queue"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default EnhancedAudioPlayer;
